import { Component, inject, OnInit, ChangeDetectionStrategy, DestroyRef } from '@angular/core';
import { Router, ActivatedRoute, RouterOutlet, NavigationEnd } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { filter, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Auth } from './services/auth';
import { Navbar } from './components/core/navbar/navbar';
import { environment } from '../environments/environment';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, Navbar],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class App implements OnInit {

  protected authService = inject(Auth);
  private titleService = inject(Title);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private toastr = inject(ToastrService);

  ngOnInit() {
    this.titleService.setTitle(environment.name);
    this.authService.initializeAuth();

    // Handle external token auto-login
    this.handleExternalTokenLogin();
  }

  private handleExternalTokenLogin(): void {
    // Listen for navigation to login pages (root or /login)
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      filter((event) => event.url.startsWith('/?token=') || event.url.startsWith('/login?token=') || event.url === '/' || event.url === '/login'),
      switchMap(() => this.route.queryParams),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(params => {
      const token = params['token'];
      if (!token) return;

      const externalToken = this.authService.decodeExternalToken(token);
      if (!externalToken?.user) return;

      const sameOperator = this.authService.currentUser()?.username === externalToken.user.username;

      if (this.authService.isLoggedIn() && sameOperator) {
        this.authService.externalTokenData.set(externalToken);
        const user = this.authService.currentUser();
        if (user?.role) {
          this.authService.navigateByRole(user.role);
        }
      } else {
        this.authService.loginWithExternalToken(token).subscribe({
          next: (response) => {
            // Staged login: 2FA-enabled accounts get no token yet — hand the
            // challenge to the login page so the user can enter their code.
            if (response.twoFactorRequired) {
              this.authService.pendingTwoFactor.set(response);
              this.toastr.info('A bejelentkezéshez adja meg a kétfaktoros azonosító kódot.');
            }
          },
          error: (err) => {
            console.error('External token login failed:', err);
            this.toastr.error('Az automatikus bejelentkezés sikertelen. Kérjük, jelentkezzen be manuálisan.');
          }
        });
      }
    });
  }
}
